(() => {
  const GIFT_PROPERTY = '_aerre_threshold_gift';
  const TIER_PROPERTY = '_tier_discount_eligible';
  const CHECKOUT_DEADLINE_MS = 3000;
  const CHECKOUT_LOG_KEY = 'aerre:checkout-handoff:v1';
  const GIFT_PENDING_KEY = 'aerre:threshold-gift-pending:v1';

  function priorityLines(state, config) {
    return (state.cart?.items || []).filter(item => Number(item.product_id) === Number(config.priority?.productId));
  }

  function priorityUpdates(state, config, selected) {
    let kept = false;
    return Object.fromEntries(priorityLines(state, config).flatMap(item => {
      const quantity = selected && !kept && Number(item.variant_id) === Number(config.priority.variantId) ? 1 : 0;
      if (quantity) kept = true;
      return Number(item.quantity) === quantity ? [] : [[item.key, quantity]];
    }));
  }

  function tierPropertyChanges(state) {
    if (!Array.isArray(state?.tierLines)) return [];
    const eligibility = new Map(state.tierLines.map(line => [line.key, line.eligible === true]));
    const items = state.cart?.items || [];
    const children = new Set(items.flatMap(item => (item.item_components || []).map(component => component.key)));
    return items.flatMap(item => {
      if (!eligibility.has(item.key) || children.has(item.key) || item.parent_relationship) return [];
      const properties = { ...(item.properties || {}) };
      const desired = eligibility.get(item.key);
      if (desired && properties[TIER_PROPERTY] !== 'true') {
        properties[TIER_PROPERTY] = 'true';
      } else if (!desired && properties[TIER_PROPERTY] != null && properties[TIER_PROPERTY] !== '') {
        delete properties[TIER_PROPERTY];
        if (!Object.keys(properties).length) properties[TIER_PROPERTY] = '';
      } else return [];
      return [{ id: item.key, quantity: item.quantity, properties }];
    });
  }

  function propertiesMatch(left, right) {
    const entries = value => Object.entries(value || {}).filter(([, entry]) => entry != null && entry !== '').map(([key, entry]) => [key, String(entry)]).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(entries(left)) === JSON.stringify(entries(right));
  }

  function giftLines(state, marker) {
    const items = state.cart?.items || [];
    const componentKeys = new Set(items.flatMap((item) => (item.item_components || []).map((component) => component.key)));
    const marked = items.filter((item) => item.properties?.[GIFT_PROPERTY] === marker && !item.item_components?.length && !item.parent_relationship && !componentKeys.has(item.key));
    return marked.map((item) => {
      const rendered = (state.giftLines || []).find((line) => line.key === item.key);
      return {
        key: item.key,
        variantId: Number(item.variant_id),
        quantity: Number(item.quantity),
        netCents: rendered ? Number(rendered.netCents) : Number(item.final_line_price)
      };
    });
  }

  function isActive(state, config) {
    return Boolean(config.active && state.active && state.country === 'AU' && state.currency === 'AUD');
  }

  function offerAttributeChanges(state, config) {
    const current = state.cart?.attributes || {};
    const desired = isActive(state, config) ? config.attributeValue : (config.controlValue || 'control');
    const changes = {};
    if (current[config.attributeKey] !== desired) changes[config.attributeKey] = desired;
    const legacyKey = config.legacyAttributeKey;
    if (legacyKey && legacyKey !== config.attributeKey && current[legacyKey] != null && current[legacyKey] !== '') {
      changes[legacyKey] = '';
    }
    return changes;
  }

  function shippingRuleValue(state, config) {
    if (state.shippingRuleEnabled !== true) return '';
    let count;
    let route = 'legacy';
    if (state.country === 'AU' && state.currency === 'AUD') {
      route = isActive(state, config) ? 'treatment' : 'control';
      if (!Array.isArray(state.tierLines) || !Array.isArray(state.cart?.items)) throw new Error('Shipping quantity unavailable');
      const eligibility = new Map(state.tierLines.map(line => [line.key, line.eligible === true]));
      const children = new Set(state.cart.items.flatMap(item => (item.item_components || []).map(component => component.key)));
      count = state.cart.items.reduce((total, item) => {
        if (children.has(item.key) || item.parent_relationship) return total;
        if (!eligibility.has(item.key)) throw new Error('Shipping line eligibility unavailable');
        const quantity = Number(item.quantity);
        if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid shipping quantity');
        return total + (eligibility.get(item.key) ? quantity : 0);
      }, 0);
    } else {
      count = Number(state.legacyShippingCount);
    }
    if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid shipping quantity');
    return route + '_' + (count >= 3 ? '3plus' : count);
  }

  function cartAttributeChanges(state, config) {
    const changes = offerAttributeChanges(state, config);
    const key = config.shippingAttributeKey;
    if (!key) return changes;
    const desired = shippingRuleValue(state, config);
    const current = state.cart?.attributes?.[key];
    if (desired ? current !== desired : current != null && current !== '') changes[key] = desired;
    return changes;
  }

  function isEligible(state, config) {
    return isActive(state, config) && Number(state.spendCents) >= Number(config.giftThreshold || 10000);
  }

  function cleanupUpdates(state, config) {
    const lines = giftLines(state, config.giftMarker);
    const variants = Array.isArray(state.allowedGiftVariantIds)
      ? state.allowedGiftVariantIds.map(Number)
      : (state.gifts || []).map((gift) => Number(gift.variantId));
    const canKeep = isEligible(state, config) && state.configured;
    const updates = {};
    let kept = false;
    for (const line of lines) {
      if (!canKeep || kept || !variants.includes(line.variantId) || line.netCents !== 0) {
        updates[line.key] = 0;
      } else {
        kept = true;
        if (line.quantity !== 1) updates[line.key] = 1;
      }
    }
    return updates;
  }

  class ThresholdOffer {
    constructor(config, environment = globalThis) {
      this.config = config;
      this.environment = environment;
      this.window = environment.window || environment;
      this.document = environment.document;
      this.queue = Promise.resolve();
      this.keyAliases = new Map();
      this.previousLineQuantities = new Map();
      this.mergedLineKeys = new Set();
      this.lineMergeEvents = [];
      this.mergeRevision = 0;
      this.state = null;
      this.replayForms = new WeakSet();
      this.seen = new Set();
      this.checkoutPending = false;
      this.cartNavigationPending = false;
      this.choosing = false;
      this.giftVerificationPending = false;
      try { this.giftVerificationPending = this.window.sessionStorage.getItem(GIFT_PENDING_KEY) === this.config.giftMarker; } catch (_) {}
      this.refreshTimer = null;
      this.error = '';
      this.dialogPurpose = '';
      this.dialogRequestId = 0;
      this.cartWrites = new Set();
      this.priorityError = '';
      this.priorityLeaving = false;
      this.checkoutLeaving = false;
      this.operationGeneration = 0;
      this.activeOperationGeneration = null;
      this.activeRequests = new Set();
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCartChanged = this.onCartChanged.bind(this);
    }

    start() {
      this.renderCheckoutLog();
      let initial = this.config.initialState;
      if (!initial) {
        try {
          const node = this.document.querySelector('script[data-aerre-threshold-state]');
          if (node) initial = JSON.parse(node.textContent);
        } catch (_) {}
      }
      if (initial) this.observeGiftVerification(initial);
      const cleanControl = !this.giftVerificationPending && initial && !isActive(initial, this.config) && !Object.keys(cartAttributeChanges(initial, this.config)).length && !giftLines(initial, this.config.giftMarker).length && !tierPropertyChanges(initial).length && !priorityLines(initial, this.config).length;
      if (initial) this.state = initial;
      this.document.addEventListener('click', this.onClick, true);
      this.document.addEventListener('submit', this.onSubmit, true);
      this.document.documentElement.addEventListener('cart:updated', this.onCartChanged);
      this.document.documentElement.addEventListener('cart:refresh', this.onCartChanged);
      this.window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.operationGeneration += 1;
          this.checkoutLeaving = false;
          this.checkoutPending = false;
          this.cartNavigationPending = false;
          this.priorityLeaving = false;
          this.priorityError = '';
          this.renderPriority();
          this.refresh().catch((error) => {
            if (error.code === 'CHECKOUT_HANDOFF') return;
            this.priorityError = 'prioritySyncFailed';
            this.renderPriority();
            this.showError('syncFailed');
          });
        }
      });
      if (this.config.priority?.enabled && this.environment.MutationObserver) {
        this.priorityObserver = new this.environment.MutationObserver(() => this.renderPriority());
        const drawer = this.document.querySelector('cart-drawer#mini-cart');
        if (drawer) this.priorityObserver.observe(drawer, { childList: true, subtree: true });
      }
      this.document.querySelector('[data-aerre-gift-dialog]')?.addEventListener('click', (event) => {
        if (event.target === event.currentTarget && !this.choosing) {
          this.dialogRequestId += 1;
          event.currentTarget.close();
        }
      });
      this.document.querySelector('[data-aerre-gift-dialog]')?.addEventListener('cancel', (event) => {
        if (this.choosing) event.preventDefault();
        else this.dialogRequestId += 1;
      });
      this.document.querySelector('[data-aerre-gift-dialog]')?.addEventListener('close', () => {
        this.dialogRequestId += 1;
        if (!this.choosing) this.dialogPurpose = '';
        if (!this.dialogTrigger) return;
        const trigger = this.dialogTrigger.isConnected ? this.dialogTrigger : this.document.querySelector('[data-aerre-gift-open]');
        trigger?.focus({ preventScroll: true });
        this.dialogTrigger = null;
      });
      this.ready = cleanControl ? Promise.resolve(initial) : this.refresh();
      this.ready.catch((error) => { if (error.code !== 'CHECKOUT_HANDOFF') this.showError('syncFailed'); });
      this.renderPriority();
      return this.ready;
    }

    observeGiftVerification(state) {
      if (giftLines(state, this.config.giftMarker).some(line => line.netCents !== 0)) {
        this.setGiftVerificationPending(true);
      }
    }

    setGiftVerificationPending(pending) {
      this.giftVerificationPending = Boolean(pending);
      try {
        if (pending) this.window.sessionStorage.setItem(GIFT_PENDING_KEY, this.config.giftMarker);
        else this.window.sessionStorage.removeItem(GIFT_PENDING_KEY);
      } catch (_) {}
    }

    trackCartWrite(promise) {
      this.cartWrites.add(promise);
      const done = () => this.cartWrites.delete(promise);
      promise.then(done, done);
      return promise;
    }

    async waitForCartWrites() {
      while (this.cartWrites.size) await Promise.allSettled([...this.cartWrites]);
    }

    canContinue(generation = this.activeOperationGeneration ?? this.operationGeneration) {
      return !this.checkoutLeaving && generation === this.operationGeneration;
    }

    assertCurrent(generation) {
      if (this.canContinue(generation)) return;
      const error = new Error('Checkout handoff started');
      error.code = 'CHECKOUT_HANDOFF';
      throw error;
    }

    renderCheckoutLog(record) {
      // Local, capped diagnostics only; never include cart or customer identifiers.
      try {
        const stored = JSON.parse(this.window.sessionStorage.getItem(CHECKOUT_LOG_KEY) || '[]');
        const records = Array.isArray(stored) ? stored.slice(record ? -19 : -20) : [];
        if (record) records.push(record);
        if (record) this.window.sessionStorage.setItem(CHECKOUT_LOG_KEY, JSON.stringify(records));
        let node = this.document.getElementById('aerre-checkout-handoff-log');
        if (!node) {
          node = this.document.createElement('script');
          node.id = 'aerre-checkout-handoff-log';
          node.type = 'application/json';
          this.document.documentElement.appendChild(node);
        }
        node.textContent = JSON.stringify(records);
      } catch (_) {}
    }

    setCartNavigationPending(pending) {
      this.cartNavigationPending = Boolean(pending);
      this.renderPriority();
    }

    renderPriority() {
      if (!this.config.priority?.enabled) return;
      const paused = Boolean(this.document.querySelector('[data-membership-paused-notice]'));
      this.document.querySelectorAll('[data-aerre-priority-panel]').forEach(panel => {
        panel.hidden = false;
        const fallback = panel.parentElement.querySelector('[data-aerre-priority-fallback]');
        if (fallback) fallback.hidden = true;
        panel.querySelectorAll('[data-aerre-priority-checkout]').forEach(button => {
          button.disabled = this.checkoutPending || this.cartNavigationPending || paused || (button.dataset.aerrePriorityCheckout === 'priority' && this.state?.priority?.available === false);
          button.setAttribute('aria-busy', String(this.checkoutPending || this.cartNavigationPending));
        });
        const notice = panel.querySelector('[data-aerre-priority-error]');
        if (notice) {
          const message = this.config.strings?.[this.priorityError] || '';
          if (notice.textContent !== message) notice.textContent = message;
          notice.hidden = !message;
        }
        const price = Number(panel.dataset.price);
        const base = Number(panel.dataset.baseTotal);
        const currency = panel.dataset.currency || this.state?.currency;
        if (!Number.isFinite(price) || !Number.isFinite(base)) return;
        try {
          const formatter = new Intl.NumberFormat(this.document.documentElement.lang || 'en-AU', { style: 'currency', currency });
          const total = panel.querySelector('[data-aerre-priority-total]');
          const fee = panel.querySelector('[data-aerre-priority-price]');
          const totalText = formatter.format((base + price) / 100);
          const feeText = formatter.format(price / 100);
          if (total && total.textContent !== totalText) total.textContent = totalText;
          if (fee && fee.textContent !== feeText) fee.textContent = feeText;
          panel.dataset.price = String(price);
        } catch (_) {}
      });
    }

    async syncPriority(state, selected) {
      if (!this.config.priority?.enabled) return { state, changed: false };
      let changed = false;
      const updates = priorityUpdates(state, this.config, selected);
      if (Object.keys(updates).length) {
        await this.write('update', { updates });
        state = await this.readState();
        changed = true;
      }
      if (selected && !priorityLines(state, this.config).length) {
        let addError;
        try {
          await this.write('add', { items: [{ id: this.config.priority.variantId, quantity: 1, properties: { _aerre_priority_checkout: 'v1' } }] });
        } catch (error) { addError = error; }
        state = await this.readState();
        changed = true;
        if (addError && !priorityLines(state, this.config).length) throw addError;
      }
      const lines = priorityLines(state, this.config);
      const valid = selected ? lines.length === 1 && Number(lines[0].quantity) === 1 && Number(lines[0].variant_id) === Number(this.config.priority.variantId) : lines.length === 0;
      if (!valid) throw new Error('Priority choice could not be confirmed');
      return { state, changed };
    }

    enqueue(operation, checkoutPreparation = false) {
      if (this.checkoutPending && !checkoutPreparation) {
        const error = new Error('Checkout handoff started');
        error.code = 'CHECKOUT_HANDOFF';
        return Promise.reject(error);
      }
      const generation = this.operationGeneration;
      const run = async () => {
        this.assertCurrent(generation);
        this.activeOperationGeneration = generation;
        try { return await operation(); }
        finally { this.activeOperationGeneration = null; }
      };
      const task = this.queue.then(run, run);
      this.queue = task.catch(() => {});
      return task;
    }

    beforeCartWrite() {
      return this.enqueue(() => this.reconcile(false)).catch((error) => {
        if (error.code === 'CHECKOUT_HANDOFF') throw error;
        this.showError('syncFailed');
        throw new Error(this.config.strings?.syncFailed || error.message);
      });
    }

    refresh() {
      return this.enqueue(() => this.reconcile(true));
    }

    async request(url, options = {}, format = 'json') {
      const generation = this.activeOperationGeneration ?? this.operationGeneration;
      this.assertCurrent(generation);
      const controller = new this.environment.AbortController();
      this.activeRequests.add(controller);
      const timeout = this.window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await this.environment.fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          ...options,
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart request failed');
        const result = format === 'text' ? await response.text() : await response.json();
        this.assertCurrent(generation);
        return result;
      } catch (error) {
        this.assertCurrent(generation);
        throw error;
      } finally {
        this.activeRequests.delete(controller);
        this.window.clearTimeout(timeout);
      }
    }

    async readState() {
      const url = new URL(this.config.cartUrl, this.window.location.origin);
      url.searchParams.set('section_id', 'aerre-threshold-state');
      const previewTheme = this.config.previewThemeId || new URL(this.window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) {
        url.searchParams.set('preview_theme_id', previewTheme);
        url.searchParams.set('_eab', '1');
      }
      const html = await this.request(url.pathname + url.search, {}, 'text');
      const parsed = new this.environment.DOMParser().parseFromString(html, 'text/html');
      const node = parsed.querySelector('[data-aerre-threshold-state]');
      if (!node) throw new Error('Offer state unavailable');
      const state = JSON.parse(node.textContent);
      if (!Array.isArray(state.cart?.items) || !Number.isFinite(Number(state.spendCents)) || !Number.isFinite(Number(state.perfumeCount))) {
        throw new Error('Invalid offer state');
      }
      if (!state.cart.token) {
        const cart = await this.request(`${this.config.cartUrl.replace(/\/$/, '')}.js`);
        if (typeof cart.token !== 'string' || !cart.token) throw new Error('Cart session unavailable');
        state.cart.token = cart.token;
      }
      state.progressTemplates = {};
      parsed.querySelectorAll('template[data-aerre-threshold-progress-template]').forEach((template) => {
        state.progressTemplates[template.dataset.aerreThresholdProgressTemplate] = template.innerHTML;
      });
      const giftOptions = parsed.querySelector('template[data-aerre-gift-options-template]');
      if (giftOptions) state.giftOptionsHtml = giftOptions.innerHTML;
      this.observeGiftVerification(state);
      return state;
    }

    async write(endpoint, payload) {
      return this.request(`${this.config.cartUrl.replace(/\/$/, '')}/${endpoint}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
    }

    resolveLineKey(key) {
      if (this.state?.cart?.items?.some(item => item.key === key)) return key;
      const seen = new Set();
      while (this.keyAliases.has(key) && !seen.has(key)) {
        seen.add(key);
        key = this.keyAliases.get(key);
      }
      return key;
    }

    resolveLineChange(key, requestedQuantity, previousQuantity, previousMergeRevision) {
      const id = this.resolveLineKey(key);
      const current = this.state?.cart?.items?.find(item => item.key === id);
      const requested = Number(requestedQuantity);
      if (!current || !Number.isFinite(requested)) {
        return { id, quantity: requestedQuantity };
      }
      if (id !== key && this.mergedLineKeys.has(key)) {
        const previous = this.previousLineQuantities.get(key);
        return { id, quantity: Math.max(0, Number(current.quantity) + requested - previous) };
      }
      // Only a confirmed property-induced merge changes the meaning of an
      // absolute quantity request. An ordinary stale cart read must not do so.
      const added = previousMergeRevision == null ? 0 : this.lineMergeEvents
        .filter(event => event.revision > previousMergeRevision && event.key === key)
        .reduce((total, event) => total + event.added, 0);
      return { id, quantity: Math.max(0, requested + added) };
    }

    rememberLineKeys(before, after, update) {
      const nextItems = after?.cart?.items || [];
      for (const item of before?.cart?.items || []) {
        if (item.parent_relationship || nextItems.some(next => next.key === item.key)) continue;
        const properties = update?.id === item.key ? update.properties : item.properties;
        const matches = nextItems.filter(next => !next.parent_relationship &&
          String(next.variant_id || next.id) === String(item.variant_id || item.id) &&
          String(next.selling_plan_allocation?.selling_plan?.id || '') === String(item.selling_plan_allocation?.selling_plan?.id || '') &&
          propertiesMatch(next.properties, properties));
        if (matches.length === 1 && matches[0].key !== item.key) {
          this.keyAliases.set(item.key, matches[0].key);
          this.previousLineQuantities.set(item.key, Number(item.quantity));
          if (update?.id === item.key && Number(matches[0].quantity) > Number(item.quantity)) {
            const mergedOthers = (before.cart.items || []).filter(other =>
              other.key !== item.key && !other.parent_relationship &&
              String(other.variant_id || other.id) === String(item.variant_id || item.id) &&
              String(other.selling_plan_allocation?.selling_plan?.id || '') === String(item.selling_plan_allocation?.selling_plan?.id || '') &&
              propertiesMatch(other.properties, update.properties));
            if (mergedOthers.length) {
              this.mergedLineKeys.add(item.key);
              this.mergeRevision += 1;
              for (const other of mergedOthers) {
                this.lineMergeEvents.push({ revision: this.mergeRevision, key: other.key, added: Number(item.quantity) });
              }
            }
          }
        }
      }
    }

    async synchronizeAttributes(state) {
      let changed = false;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const attributes = cartAttributeChanges(state, this.config);
        if (!Object.keys(attributes).length) return { state, changed };
        const previous = state;
        await this.write('update', { attributes });
        state = await this.readState();
        this.rememberLineKeys(previous, state);
        changed = true;
      }
      if (Object.keys(cartAttributeChanges(state, this.config)).length) throw new Error('Offer assignment could not be confirmed');
      return { state, changed };
    }

    async reconcile(showPopup, keepPriority = this.priorityLeaving) {
      let state = await this.readState();
      this.rememberLineKeys(this.state, state);
      let changed = false;
      if (!keepPriority) {
        const cleaned = await this.syncPriority(state, false);
        state = cleaned.state;
        changed = cleaned.changed;
      }
      const assigned = await this.synchronizeAttributes(state);
      state = assigned.state;
      changed = changed || assigned.changed;
      const tierAttempts = (state.cart.items || []).length + 3;
      for (let attempt = 0; attempt < tierAttempts; attempt += 1) {
        const update = tierPropertyChanges(state)[0];
        if (!update) break;
        const previous = state;
        await this.write('change', update);
        state = await this.readState();
        this.rememberLineKeys(previous, state, update);
        changed = true;
      }
      if (tierPropertyChanges(state).length) throw new Error('Tier eligibility could not be confirmed');
      let removed = false;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const updates = cleanupUpdates(state, this.config);
        if (!Object.keys(updates).length) break;
        removed = true;
        await this.write('update', { updates });
        state = await this.readState();
        changed = true;
      }
      if (Object.keys(cleanupUpdates(state, this.config)).length) throw new Error('Gift cleanup could not be confirmed');
      const finalAssignment = await this.synchronizeAttributes(state);
      state = finalAssignment.state;
      changed = changed || finalAssignment.changed;
      if (tierPropertyChanges(state).length || Object.keys(cleanupUpdates(state, this.config)).length) throw new Error('Cart changed during offer assignment');
      this.setGiftVerificationPending(false);
      this.setState(state);
      if (changed || this.error === 'syncFailed' || this.error === 'giftVerificationPending') await this.publish(state);
      if (this.error === 'syncFailed' || this.error === 'giftVerificationPending') {
        this.clearError();
        if (this.dialogPurpose === 'error') {
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
          this.dialogPurpose = '';
          this.status('');
        }
      }
      if (removed && !this.error) this.status('removed');
      if (showPopup) this.maybeOpen();
      return state;
    }

    setState(state) {
      if (!this.canContinue()) return;
      this.state = state;
      if (state.cart) {
        const base = Math.max(0, Number(state.cart.total_price) - priorityLines(state, this.config).reduce((sum, item) => sum + Number(item.final_line_price), 0));
        this.document.querySelectorAll('[data-aerre-priority-panel]').forEach(panel => {
          if (Number.isFinite(base)) panel.dataset.baseTotal = String(base);
          if (state.priority?.price != null) panel.dataset.price = String(state.priority.price);
          if (state.currency) panel.dataset.currency = state.currency;
        });
      }
      this.renderPriority();
      const giftOptions = this.document.querySelector('.aerre-threshold-gift-options');
      let focusedGift;
      if (giftOptions && typeof state.giftOptionsHtml === 'string' && state.giftOptionsHtml !== this.renderedGiftOptionsHtml) {
        const focused = this.document.activeElement;
        if (focused && giftOptions.contains(focused)) focusedGift = focused.closest('[data-aerre-gift-choose]')?.dataset.variantId;
        giftOptions.innerHTML = state.giftOptionsHtml;
        this.renderedGiftOptionsHtml = state.giftOptionsHtml;
      }
      for (const surface of ['cart', 'announcement', 'drawer', 'drawer-gift']) {
        if (!state.progressTemplates?.[surface]) continue;
        const template = this.document.createElement('template');
        template.innerHTML = state.progressTemplates[surface];
        const replacement = template.content.querySelector(`[data-aerre-threshold-progress="${surface}"]`);
        if (!replacement) continue;
        this.document.querySelectorAll(`[data-aerre-threshold-progress="${surface}"]`).forEach((current) => {
          current.replaceWith(replacement.cloneNode(true));
        });
      }
      this.document.querySelectorAll('[data-aerre-gift-cart-status]').forEach((node) => {
        const text = this.config.strings?.[this.cartNotice] || '';
        node.textContent = text;
        node.hidden = !text;
      });
      this.document.documentElement.dispatchEvent(new this.environment.CustomEvent('aerre:threshold-state', {
        bubbles: true,
        detail: { state }
      }));
      const eligible = isEligible(state, this.config);
      const selected = giftLines(state, this.config.giftMarker)[0];
      this.document.querySelectorAll('[data-aerre-gift-choose]').forEach((button) => {
        const gift = state.gifts?.find((entry) => Number(entry.variantId) === Number(button.dataset.variantId));
        button.disabled = this.choosing || !eligible || !state.configured || !gift?.available;
        button.setAttribute('aria-pressed', String(Number(selected?.variantId) === Number(button.dataset.variantId)));
        if (focusedGift === button.dataset.variantId && !button.disabled) button.focus({ preventScroll: true });
      });
      if (isActive(state, this.config) && this.dialogPurpose === 'gift' && !this.error && !eligible && this.document.querySelector('[data-aerre-gift-dialog]')?.open && !this.checkoutPending) {
        this.status('notEligible');
      }
    }

    sectionTargets() {
      const targets = [];
      const drawer = this.document.querySelector('cart-drawer#mini-cart');
      if (drawer) targets.push({ id: drawer.getAttribute('section') || 'mini-cart', drawer: true });
      const page = this.document.querySelector('[data-aerre-rewards-cart-section]')?.closest('[id^="shopify-section-"]');
      if (page) targets.push({ id: page.id.replace('shopify-section-', ''), element: page });
      return targets;
    }

    async publish(state) {
      const targets = this.sectionTargets();
      if (targets.length) {
        const url = new URL(this.window.location.href);
        url.searchParams.delete('section_id');
        url.searchParams.set('sections', targets.map((target) => target.id).join(','));
        const sections = await this.request(url.pathname + url.search);
        for (const target of targets) {
          if (typeof sections[target.id] !== 'string') throw new Error('Cart section unavailable');
          if (!target.drawer) {
            const parsed = new this.environment.DOMParser().parseFromString(sections[target.id], 'text/html');
            const replacement = parsed.getElementById(`shopify-section-${target.id}`);
            if (!replacement) throw new Error('Cart section unavailable');
            target.element.replaceChildren(...replacement.childNodes);
          }
        }
        const drawer = targets.find((target) => target.drawer);
        if (drawer) sections['mini-cart'] = sections[drawer.id];
        state.cart.sections = sections;
      }
      for (const name of ['cart:updated', 'cart:refresh']) {
        this.document.documentElement.dispatchEvent(new this.environment.CustomEvent(name, {
          bubbles: true,
          detail: { cart: state.cart, aerreThresholdOffer: true, replacementDelay: 1 }
        }));
      }
      this.setState(state);
    }

    sessionKey() {
      const token = this.state?.cart?.token;
      return token ? `aerre:threshold-gift:${String(token).split('?')[0]}` : null;
    }

    wasSeen() {
      const key = this.sessionKey();
      if (!key || this.seen.has(key)) return true;
      try { return this.window.sessionStorage.getItem(key) === '1'; } catch (_) { return false; }
    }

    markSeen() {
      const key = this.sessionKey();
      if (!key) return;
      this.seen.add(key);
      try { this.window.sessionStorage.setItem(key, '1'); } catch (_) {}
    }

    maybeOpen() {
      if (!this.canContinue() || this.checkoutPending) return;
      if (this.error || !this.state || !isEligible(this.state, this.config)) return;
      if (giftLines(this.state, this.config.giftMarker).length || this.wasSeen()) return;
      this.status(this.state.configured && this.state.gifts?.some((gift) => gift.available) ? '' : 'unavailable');
      this.showDialog();
      this.markSeen();
    }

    showDialog(purpose = 'gift', generation = this.activeOperationGeneration ?? this.operationGeneration) {
      if (!this.canContinue(generation) || this.checkoutPending) return;
      this.dialogPurpose = purpose;
      const dialog = this.document.querySelector('[data-aerre-gift-dialog]');
      if (dialog && !dialog.open) dialog.showModal();
    }

    status(key) {
      const text = this.config.strings?.[key] || '';
      this.document.querySelectorAll('[data-aerre-gift-status]').forEach((node) => {
        node.textContent = text;
        node.hidden = !text;
      });
      if (key === 'removed' || key === 'added') {
        this.cartNotice = key;
        this.document.querySelectorAll('[data-aerre-gift-cart-status]').forEach((node) => {
          node.textContent = text;
          node.hidden = !text;
        });
      }
    }

    showError(key, generation = this.activeOperationGeneration ?? this.operationGeneration) {
      if (!this.canContinue(generation) || this.checkoutPending) return;
      this.error = key;
      this.status(key);
      this.document.querySelectorAll('[data-aerre-gift-retry]').forEach((button) => { button.hidden = false; });
      this.showDialog('error', generation);
    }

    clearError() {
      this.error = '';
      this.document.querySelectorAll('[data-aerre-gift-retry]').forEach((button) => { button.hidden = true; });
    }

    async retryDialog() {
      if (this.error && this.error !== 'syncFailed' && this.state && isActive(this.state, this.config)) {
        return this.openGiftChooser();
      }
      try {
        await this.enqueue(() => this.reconcile(false));
      } catch (_) {
        this.showError('syncFailed');
      }
    }

    async openGiftChooser() {
      if (!this.config.active || (this.state && !isActive(this.state, this.config))) return;
      const dialogRequest = ++this.dialogRequestId;
      this.clearError();
      this.showDialog();
      this.status('loading');
      try {
        const state = await this.enqueue(() => this.reconcile(false));
        if (dialogRequest !== this.dialogRequestId) return;
        if (!isActive(state, this.config)) {
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
          this.dialogPurpose = '';
          return;
        }
        if (!isEligible(state, this.config)) this.status('notEligible');
        else if (!state.configured || !state.gifts?.some((gift) => gift.available)) this.status('unavailable');
        else {
          this.status('');
          this.markSeen();
        }
      } catch (_) {
        if (dialogRequest !== this.dialogRequestId) return;
        this.showError(this.giftVerificationPending ? 'giftVerificationPending' : 'syncFailed');
      }
    }

    async removeAllGifts() {
      let state = await this.readState();
      const lines = giftLines(state, this.config.giftMarker);
      if (lines.length) {
        await this.write('update', { updates: Object.fromEntries(lines.map((line) => [line.key, 0])) });
        state = await this.readState();
      }
      if (giftLines(state, this.config.giftMarker).length) throw new Error('Gift removal could not be confirmed');
      this.setGiftVerificationPending(false);
      return state;
    }

    chooseGift(variantId) {
      if (this.choosing) return this.queue;
      this.clearError();
      this.dialogPurpose = 'gift';
      this.choosing = true;
      if (this.state) this.setState(this.state);
      this.status('choosing');
      return this.enqueue(async () => {
        let added = false;
        let giftStateRead = false;
        let giftConfirmed = false;
        let failureKey = 'addFailed';
        try {
          let state = await this.reconcile(false);
          const variant = state.gifts?.find((gift) => Number(gift.variantId) === Number(variantId));
          if (!isEligible(state, this.config)) {
            failureKey = 'notEligible';
            throw new Error('Gift not eligible');
          }
          if (!state.configured || !variant?.available) {
            failureKey = state.gifts?.some(gift => gift.available) ? 'availabilityChanged' : 'unavailable';
            throw new Error('Gift unavailable');
          }
          const existing = giftLines(state, this.config.giftMarker);
          if (existing.length === 1 && existing[0].variantId === Number(variantId)) {
            this.document.querySelector('[data-aerre-gift-dialog]')?.close();
            return;
          }
          await this.removeAllGifts();
          added = true;
          this.setGiftVerificationPending(true);
          let addError;
          try {
            await this.write('add', { items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_PROPERTY]: this.config.giftMarker } }] });
          } catch (error) { addError = error; }
          for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
              state = await this.readState();
              giftStateRead = true;
              break;
            } catch (error) {
              if (attempt === 1) throw error;
            }
          }
          const selected = giftLines(state, this.config.giftMarker);
          if (addError && !selected.length) {
            const stillAvailable = state.gifts?.some(gift => Number(gift.variantId) === Number(variantId) && gift.available);
            if (!stillAvailable) failureKey = state.gifts?.some(gift => gift.available) ? 'availabilityChanged' : 'unavailable';
            throw addError;
          }
          if (!isEligible(state, this.config) || !state.configured || !(state.allowedGiftVariantIds || state.gifts?.map(gift => gift.variantId) || []).map(Number).includes(Number(variantId)) || selected.length !== 1 || selected[0].variantId !== Number(variantId) || selected[0].quantity !== 1 || selected[0].netCents !== 0) {
            failureKey = 'chargedGift';
            throw new Error('Free gift discount could not be confirmed');
          }
          giftConfirmed = true;
          this.setGiftVerificationPending(false);
          this.clearError();
          this.setState(state);
          await this.publish(state);
          this.markSeen();
          this.status('added');
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
        } catch (_) {
          if (!this.canContinue()) return;
          if (giftConfirmed || (added && !giftStateRead)) {
            failureKey = 'syncFailed';
          } else {
            try {
              const state = added ? await this.removeAllGifts() : await this.readState();
              this.setState(state);
              await this.publish(state);
            } catch (_) {
              failureKey = 'syncFailed';
            }
          }
          this.showError(this.giftVerificationPending ? 'giftVerificationPending' : failureKey);
        } finally {
          this.choosing = false;
          if (this.state) this.setState(this.state);
        }
      });
    }

    onCartChanged(event) {
      if (event.detail?.aerreThresholdOffer || this.checkoutPending || this.cartNavigationPending) return;
      this.window.clearTimeout(this.refreshTimer);
      this.refreshTimer = this.window.setTimeout(() => {
        this.refresh().catch((error) => { if (error.code !== 'CHECKOUT_HANDOFF') this.showError('syncFailed'); });
      }, 30);
    }

    block(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    async prepareCheckoutPriority(selected, displayedPrice, attempt) {
      if (!this.config.priority?.enabled) return;
      attempt.stage = 'cart-read';
      let cart = await this.request(`${this.config.cartUrl.replace(/\/$/, '')}.js`);
      if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
      let state = { cart };
      this.observeGiftVerification(state);
      if (this.giftVerificationPending) {
        attempt.stage = 'pending-gift-verification';
        state = await this.reconcile(false);
        if (this.giftVerificationPending) throw new Error('Gift verification pending');
        cart = state.cart;
      }
      const rememberPriority = () => {
        const lines = priorityLines(state, this.config);
        attempt.knownPriority = !lines.length ? 'absent' :
          lines.length === 1 && Number(lines[0].quantity) === 1 && Number(lines[0].variant_id) === Number(this.config.priority.variantId) ? 'present' : 'other';
      };
      rememberPriority();
      if (selected && (!this.state?.priority?.available ||
          (Number.isFinite(displayedPrice) && displayedPrice !== Number(this.state.priority.price)) ||
          this.document.querySelector('[data-membership-paused-notice]') ||
          !cart.items.some(item => Number(item.product_id) !== Number(this.config.priority.productId) && !item.properties?._aerre_threshold_gift && !item.properties?._aerre_rewards_gift))) {
        throw new Error('Priority unavailable');
      }
      const updates = priorityUpdates(state, this.config, selected);
      if (Object.keys(updates).length) {
        attempt.stage = 'priority-update';
        attempt.knownPriority = 'unknown';
        cart = await this.write('update', { updates });
        if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
        state = { cart };
        rememberPriority();
      }
      if (selected && !priorityLines(state, this.config).length) {
        attempt.stage = 'priority-add';
        attempt.addAttempted = true;
        attempt.knownPriority = 'unknown';
        // A lost response does not prove failure. Never repeat or undo this add.
        await this.write('add', { items: [{ id: this.config.priority.variantId, quantity: 1, properties: { _aerre_priority_checkout: 'v1' } }] });
        attempt.stage = 'priority-confirm';
        cart = await this.request(`${this.config.cartUrl.replace(/\/$/, '')}.js`);
        if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
        state = { cart };
        rememberPriority();
      }
      if (attempt.knownPriority !== (selected ? 'present' : 'absent')) throw new Error('Priority choice unconfirmed');
    }

    async checkout(resume, priority = false) {
      if (this.checkoutPending || this.cartNavigationPending) return;
      this.checkoutPending = true;
      this.priorityError = '';
      const generation = this.operationGeneration;
      const now = () => this.window.performance?.now() ?? Date.now();
      const started = now();
      const attempt = { selected: priority ? 'priority' : 'standard', stage: 'pending-cart', knownPriority: 'unknown', addAttempted: false };
      const displayedPrice = Number(this.document.querySelector('[data-aerre-priority-panel]')?.dataset.price);
      this.renderPriority();
      this.window.clearTimeout(this.refreshTimer);
      let deadline;
      const preparation = (async () => {
        await this.waitForCartWrites();
        this.assertCurrent(generation);
        attempt.stage = 'pending-sync';
        await this.enqueue(async () => {
          if (this.giftVerificationPending) {
            attempt.stage = 'pending-gift-verification';
            await this.reconcile(false);
            if (this.giftVerificationPending) throw new Error('Gift verification pending');
          }
          this.priorityLeaving = true;
          await this.prepareCheckoutPriority(priority, displayedPrice, attempt);
        }, true);
      })().then(() => 'prepared', () => 'fallback');
      const status = await Promise.race([
        preparation,
        new Promise(resolve => { deadline = this.window.setTimeout(() => resolve('timeout'), CHECKOUT_DEADLINE_MS); })
      ]);
      this.window.clearTimeout(deadline);
      if (this.giftVerificationPending) {
        this.operationGeneration += 1;
        for (const controller of this.activeRequests) controller.abort();
        this.checkoutPending = false;
        this.priorityLeaving = false;
        this.renderPriority();
        this.showError('giftVerificationPending', this.operationGeneration);
        this.renderCheckoutLog({ ...attempt, status: 'gift-verification-required', durationMs: Math.round(now() - started), deadlineMs: CHECKOUT_DEADLINE_MS });
        return;
      }
      // Stop old queue continuations before navigating. Aborting a request cannot
      // undo a write already received by Shopify; checkout uses that actual cart.
      this.checkoutLeaving = true;
      this.operationGeneration += 1;
      this.window.clearTimeout(this.refreshTimer);
      for (const controller of this.activeRequests) controller.abort();
      this.renderCheckoutLog({ ...attempt, status, durationMs: Math.round(now() - started), deadlineMs: CHECKOUT_DEADLINE_MS });
      try { resume(); }
      catch (_) {
        this.window.location.assign(`${this.config.rootUrl.replace(/\/$/, '')}/checkout`);
      }
    }

    onClick(event) {
      const target = event.target?.closest ? event.target : event.target?.parentElement;
      if (!target) return;
      const priorityButton = target.closest('[data-aerre-priority-checkout]');
      if (priorityButton) {
        this.block(event);
        if (!priorityButton.disabled) void this.checkout(() => this.window.location.assign(`${this.config.rootUrl.replace(/\/$/, '')}/checkout`), priorityButton.dataset.aerrePriorityCheckout === 'priority');
        return;
      }
      if ((this.checkoutPending || this.cartNavigationPending) && target.closest('line-item-quantity, [data-aerre-gift-open], [data-aerre-gift-choose], [data-aerre-gift-retry]')) {
        this.block(event);
        return;
      }
      const chooser = target.closest('[data-aerre-gift-open]');
      if (target.closest('[data-aerre-gift-retry]')) {
        this.block(event);
        void this.retryDialog();
        return;
      }
      if (chooser) {
        this.block(event);
        this.dialogTrigger = chooser;
        void this.openGiftChooser();
        return;
      }
      if (target.closest('[data-aerre-gift-close]')) {
        this.block(event);
        if (!this.choosing) {
          this.dialogRequestId += 1;
          this.document.querySelector('[data-aerre-gift-dialog]')?.close();
        }
        return;
      }
      const choice = target.closest('[data-aerre-gift-choose]');
      if (choice) {
        this.block(event);
        if (!choice.disabled) void this.chooseGift(choice.dataset.variantId);
        return;
      }
      const link = target.closest('a[href]');
      if (link) {
        const url = new URL(link.href, this.window.location.href);
        if (url.origin === this.window.location.origin && /\/checkouts?(?:\/|$)/.test(url.pathname)) {
          this.block(event);
          void this.checkout(() => this.window.location.assign(url.href));
        }
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (this.replayForms.has(form)) {
        this.replayForms.delete(form);
        return;
      }
      const action = new URL(form.action || this.window.location.href, this.window.location.href);
      const checkout = event.submitter?.name === 'checkout' || form.querySelector('input[name="checkout"]') || /\/checkouts?(?:\/|$)/.test(action.pathname);
      const nativeProductForm = !form.matches('form[is="product-form"]') || this.window.themeVariables?.settings?.cartType === 'page' || this.window.themeVariables?.settings?.pageType === 'cart';
      const nativeCartWrite = nativeProductForm && /\/cart\/(add|change|update)(?:\.js)?\/?$/.test(action.pathname);
      if (!checkout && !nativeCartWrite) return;
      this.block(event);
      if (this.checkoutPending || this.cartNavigationPending) return;
      const submitter = event.submitter;
      const resume = () => {
        const currentForm = form.isConnected ? form : (form.id ? this.document.getElementById(form.id) : null);
        if (!currentForm) {
          throw new Error('Checkout form unavailable');
        }
        const currentSubmitter = submitter?.form === currentForm ? submitter : Array.from(currentForm.elements).find((element) => element.type === 'submit' && element.name === submitter?.name && element.value === submitter?.value);
        this.replayForms.add(currentForm);
        if (currentSubmitter && !currentSubmitter.disabled) currentForm.requestSubmit(currentSubmitter);
        else currentForm.requestSubmit();
      };
      if (checkout) void this.checkout(() => this.window.location.assign(`${this.config.rootUrl.replace(/\/$/, '')}/checkout`));
      else {
        const generation = this.operationGeneration;
        void this.beforeCartWrite().then(() => { this.assertCurrent(generation); resume(); }).catch((error) => {
          if (error.code !== 'CHECKOUT_HANDOFF') this.showError('syncFailed');
        });
      }
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ThresholdOffer, giftLines, isActive, isEligible, cleanupUpdates, tierPropertyChanges, priorityLines, priorityUpdates, shippingRuleValue, cartAttributeChanges };
  } else {
    const node = document.getElementById('aerre-threshold-config');
    if (!node || node.dataset.started) return;
    node.dataset.started = 'true';
    try {
      const controller = new ThresholdOffer(JSON.parse(node.textContent));
      window.aerreThresholdOffer = controller;
      void controller.start();
    } catch (_) {
      document.querySelectorAll('[data-aerre-gift-status]').forEach((status) => {
        status.hidden = false;
      });
    }
  }
})();
